import * as Actions from "../../actions/global/global";
import { setUserLanguage } from "../../../util/userLanguageUtils";
import { restorationFinancialStatuses } from "../../../constants/appConstants";

// Helper function to calculate technician appointment access
const calculateTechnicianAppointmentValidAccess = (userType, regionId, vztSegment, brands, accountFinancialStatus) => {
  // Allow users to access Technician appointment page based on below conditions
  // 1. User has admin access
  // 2. Region belongs to US
  // 3. For non-government: always allowed
  // 4. For government: only Sourcewell brand allowed
  // 5. Account financial status should be Active or Reactivated
  if (typeof userType !== "string" || typeof regionId !== "string") {
    return false;
  }

  const hasAdminAccess = userType === "2" || userType === "4";
  const isUSRegion = regionId === "840";

  if (!hasAdminAccess || !isUSRegion) {
    return false;
  }

  // Check segment and brand requirements
  const segment = vztSegment || "";
  const brandList = (brands || "").split(";").map((brand) => brand.trim().toLowerCase());
  const isGovernmentSegment = segment.toLowerCase() === "government";
  const hasSourcewellBrand = brandList.includes("sourcewell");

  // Check account financial status requirements
  const financialStatus = (accountFinancialStatus || "").toLowerCase();
  const hasValidFinancialStatus = restorationFinancialStatuses.includes(financialStatus);

  // Government segment must have Sourcewell brand and valid financial status
  // Non-government segments must have valid financial status
  return (!isGovernmentSegment || hasSourcewellBrand) && hasValidFinancialStatus;
};

const getBankDetailsFromAPI = (res) => {
  return res?.data?.bankDetails || {};
};

const initialState = {
  isLoading: false,
  error: false,
  userName: "",
  accountNumber: "",
  erpBusinessPartner: {},
  businessPartnerNumber: "",
  accountFinancialStatus: "",
  vztSegment: "",
  brands: "",
  paymentStatus: {},
  account_id: "",
  fullName: "",
  firstName: "",
  surname: "",
  emailAddress: "",
  companyName: "",
  userLanguage: "en-US",
  regionId: "",
  cookie: "",
  accountUserTokenDetails: "",
  jwtToken: "",
  jwtRefreshToken: "",
  sessionExpired: false,
  logout: false,
  revealSessionExpired: false,
  userId: "",
  encrypId: "",
  firstPageLoader: false,
  timersRunning: false,
  userType: "",
  customerPortalPermission: true,
  accountProfilePermission: true,
  viewInvoicesPermission: true,
  managePaymentsPermission: true,
  viewFinancialCasesPermission: true,
  viewallotherCasesPermission: true,
  manageyourcontractandservicesPermission: true,
  activeCookie: false,
  revealPermissionFallBack: false,
  caseIdGlobal: "",
  filesTryAgainModal: false,
  fileTryAgainSuccess: false,
  bannerMessage: "",
  bannerDisplayOnLanding: false,
  bannerDisplayOnAll: false,
  message: "",
  plannedMaintenance: "",
  displayPlannedMaintenance: false,
  windowSize: {},
  accountName: "",
  showSecondPage: false,
  enableSecondPage: false,
  contractAccountCountry: "",
  phoneNumber: null,
  headerAriaHidden: false,
  preferredSelectedDate: null,
  enableMetricsDashboard: false,
  technicianAppointmentValidAccess: false,
  isSourcewellGovernment: false,
  bankDetails: {},
};

const globalReducer = (state = initialState, action) => {
  switch (action.type) {
    case Actions.LOGIN:
    case Actions.VALIDATECONTRACTNUMBER:
    case Actions.GET_CONTRACT_NUMBERS:
    case Actions.GET_PERMISSION: {
      return {
        ...state,
        isLoading: true,
      };
    }
    case Actions.SET_REFRESH_TOKEN: {
      return {
        ...state,
        jwtRefreshToken: action.data.refreshToken,
        encrypId: action.data.decryptValue,
      };
    }
    case Actions.SET_JWT_TOKEN: {
      return {
        ...state,
        jwtToken: action.jwtToken,
      };
    }
    case Actions.ACCOUNT_USER_TOKEN: {
      const newState = {
        ...state,
        isLoading: false,
        accountUserTokenDetails: action.accountUserTokenDetails,
        accountNumber: action.accountUserTokenDetails.universalAccountId,
        fullName: action.accountUserTokenDetails.fullName,
        firstName: action.accountUserTokenDetails.firstName,
        surname: action.accountUserTokenDetails.surname,
        emailAddress: action.accountUserTokenDetails.emailAddress,
        companyName: "",
        userLanguage: setUserLanguage(action.accountUserTokenDetails),
        regionId: action.accountUserTokenDetails.accountRegionId,
        jwtToken: action.accountUserTokenDetails.accessToken,
        jwtRefreshToken: action.accountUserTokenDetails.refreshToken,
        userId: action.accountUserTokenDetails.userId,
        encrypId: action.accountUserTokenDetails.decryptValue,
        userType: action.accountUserTokenDetails.userType,
        accountName: action.accountUserTokenDetails.accountName,
        phoneNumber: action.accountUserTokenDetails.phoneNumber,
        enableMetricsDashboard: action.accountUserTokenDetails.enableMetricsDashboard,
      };

      newState.technicianAppointmentValidAccess = calculateTechnicianAppointmentValidAccess(
        newState.userType,
        newState.regionId,
        newState.vztSegment,
        newState.brands,
        newState.accountFinancialStatus
      );

      return newState;
    }
    case Actions.LOGIN_SUCCESS:
    case Actions.LOGIN_FAILURE: {
      return {
        ...state,
        isLoading: false,
        error: action.type === Actions.LOGIN_FAILURE ? action.error : state.error,
        firstPageLoader: false,
      };
    }
    case Actions.GET_COOKIE: {
      return {
        ...state,
        isLoading: true,
        cookie: action.cookie,
      };
    }
    case Actions.CLEARACCOUNTID: {
      return {
        ...state,
        account_id: "",
      };
    }
    case Actions.CLEARJWTTOKEN: {
      return {
        ...state,
        jwtToken: "",
        jwtRefreshToken: "",
      };
    }
    case Actions.SETLOADER: {
      return {
        ...state,
        isLoading: action.value,
      };
    }
    case Actions.GETACCOUNTID: {
      return {
        ...state,
        account_id: action.account_id,
      };
    }
    case Actions.VALIDATECONTRACTNUMBER_SUCCESS: {
      return {
        ...state,
        isLoading: false,
        erpBusinessPartner: action.data,
        businessPartnerNumber: action.data.data.data[0].Account__r.ERP_Business_Partner_Number__c,
        error: false,
      };
    }
    case Actions.VALIDATECONTRACTNUMBER_FAILURE: {
      return {
        ...state,
        isLoading: false,
        erpBusinessPartner: {},
        error: action.error,
      };
    }
    case Actions.SET_SESSION_EXPIRED: {
      return {
        ...state,
        sessionExpired: action.sessionExpired,
      };
    }
    case Actions.SET_REVEAL_SESSION_EXPIRED: {
      return {
        ...state,
        revealSessionExpired: action.sessionExpired,
      };
    }
    case Actions.SET_LOGOUT: {
      return {
        ...state,
        logout: action.data,
      };
    }

    case Actions.SET_FIRST_PAGE_LOADER: {
      return {
        ...state,
        firstPageLoader: action.data,
      };
    }

    case Actions.SET_TIMERS_RUNNING: {
      return {
        ...state,
        timersRunning: action.timerStatus,
      };
    }
    case Actions.SET_PERMISSION:
    case Actions.SET_ACTIVE_COOKIE:
    case Actions.SET_REVEAL_PERMISSION_FALLBACK:
    case Actions.SET_CASE_NUMBER_GLOBAL:
    case Actions.SET_FILE_TRYAGAIN_MODAL:
    case Actions.SET_FILE_TRYAGAIN_SUCCESS:
    case Actions.BANNER_MESSAGE:
    case Actions.SET_PLANNED_MAINTENANCE:
    case Actions.BANNER_DISPLAY_ON_LANDING:
    case Actions.BANNER_DISPLAY_ON_ALL:
    case Actions.DISPLAY_PLANNED_MAINTENANCE:
    case Actions.SET_WINDOW_WIDTH_HEIGHT:
    case Actions.SET_ENCRYPT_ID:
    case Actions.SET_CONTRACT_ACCOUNT_COUNTRY:
    case Actions.SET_HEADER_ARIA_HIDDEN:
    case Actions.SET_PREFERRED_SELECTED_DATE: {
      const fieldMap = {
        [Actions.SET_PERMISSION]: { [action.name]: action.value },
        [Actions.SET_ACTIVE_COOKIE]: { activeCookie: action.value },
        [Actions.SET_REVEAL_PERMISSION_FALLBACK]: { revealPermissionFallBack: action.value },
        [Actions.SET_CASE_NUMBER_GLOBAL]: { caseIdGlobal: action.value },
        [Actions.SET_FILE_TRYAGAIN_MODAL]: { filesTryAgainModal: action.value },
        [Actions.SET_FILE_TRYAGAIN_SUCCESS]: { fileTryAgainSuccess: action.value },
        [Actions.BANNER_MESSAGE]: { bannerMessage: action.value },
        [Actions.SET_PLANNED_MAINTENANCE]: { plannedMaintenance: action.value },
        [Actions.BANNER_DISPLAY_ON_LANDING]: { bannerDisplayOnLanding: action.value },
        [Actions.BANNER_DISPLAY_ON_ALL]: { bannerDisplayOnAll: action.value },
        [Actions.DISPLAY_PLANNED_MAINTENANCE]: { displayPlannedMaintenance: action.value },
        [Actions.SET_WINDOW_WIDTH_HEIGHT]: { windowSize: action.value },
        [Actions.SET_ENCRYPT_ID]: { encrypId: action.encryptValue },
        [Actions.SET_CONTRACT_ACCOUNT_COUNTRY]: { contractAccountCountry: action.contractAccountCountry },
        [Actions.SET_HEADER_ARIA_HIDDEN]: { headerAriaHidden: action.val },
        [Actions.SET_PREFERRED_SELECTED_DATE]: { preferredSelectedDate: action.val },
      };
      return {
        ...state,
        ...fieldMap[action.type],
      };
    }
    case Actions.RESET: {
      return initialState;
    }
    case Actions.GET_CONTRACT_NUMBERS_SUCCESS: {
      const newState = {
        ...state,
        isLoading: false,
        erpBusinessPartner: action.data.data,
        businessPartnerNumber: action.data.businessPartnerNumber,
        accountFinancialStatus: action.data.accountFinancialStatus,
        account_id: action.data.accountID,
        vztSegment: action.data.vztSegment,
        brands: action.data.brands || "",
        error: false,
        isSourcewellGovernment:
          (action.data?.vztSegment || "").toLowerCase() === "government" && (action.data?.brands || "").toLowerCase().includes("sourcewell"),
        bankDetails: getBankDetailsFromAPI(action.data?.data),
      };

      newState.technicianAppointmentValidAccess = calculateTechnicianAppointmentValidAccess(
        newState.userType,
        newState.regionId,
        newState.vztSegment,
        newState.brands,
        newState.accountFinancialStatus
      );

      return newState;
    }
    case Actions.GET_CONTRACT_NUMBERS_FAILURE: {
      return {
        ...state,
        isLoading: false,
        accountFinancialStatus: "",
        erpBusinessPartner: {},
        error: action.error,
      };
    }
    case Actions.GET_PAYMENT_STATUS_SUCCESS: {
      return {
        ...state,
        isLoading: false,
        paymentStatus: action.data.data,
        error: false,
      };
    }
    case Actions.GET_PAYMENT_STATUS_FAILURE: {
      return {
        ...state,
        isLoading: false,
        paymentStatus: {},
        error: action.error,
      };
    }
    case Actions.SET_SECOND_PAGE: {
      return {
        ...state,
        showSecondPage: action.showValue,
        enableSecondPage: action.enableValue,
      };
    }

    default:
      return state;
  }
};
export default globalReducer;
